import { sendAnalytics } from '@audio-network/analytics'
import {
	Container,
	Heading,
	Paragraph,
	PlayButton,
} from '@audio-network/componentlibrary'
import { Button } from '@audio-network/componentlibrary'
import { Spotify } from '@audio-network/componentlibrary/icons'
import { usePlayer } from '@audio-network/player'
import Image from 'next/image'
import { useTranslation } from 'next-i18next'
import React from 'react'

import optimizeContentfulImage from '@/utils/contentful/optimizeContentfulImage'

import styles from './Header.module.css'
import { HeaderProps } from './Header.types'

const PLAYLIST_ARTWORK_WIDTH = 448

const Header = ({ playlist, ...props }: HeaderProps): JSX.Element => {
	const { currentTrack, isPlaying, playNextTrack, simpleTogglePlay, queue } =
		usePlayer()
	const { t } = useTranslation(['playlist'])

	const handleSpotifyAnalytics = (): void => {
		sendAnalytics({
			noun: 'Spotify',
			pastTenseVerb: 'URL Clicked',
		})
	}

	const isPlaylistPlaying =
		isPlaying &&
		playlist.trackIds
			.split(',')
			.some((track) => parseInt(track, 10) === currentTrack?.id)

	const togglePlayAll = (): void => {
		if (isPlaying && !isPlaylistPlaying) {
			playNextTrack()
		} else {
			simpleTogglePlay()
		}
		sendAnalytics({
			noun: 'Play All',
			pastTenseVerb: 'Clicked',
		})
	}

	return (
		<header className={styles.header} {...props}>
			<div className={styles.headerBackgroundWrapper}>
				<Image
					alt=""
					className={styles.headerBackgroundImage}
					priority
					src={optimizeContentfulImage(playlist.background.url, {
						quality: 50,
					})}
					fill
				/>
			</div>

			<Container className={styles.headerContainer} maxWidth="large">
				<div className={styles.headerTextWrapper}>
					<Heading color="white" level={1} size="L" uppercase>
						<span className={styles.headerTag}>{t('playlist')}</span>
						{playlist.title}
					</Heading>

					<Paragraph color="white">
						{playlist.longDescription ?? playlist.description}
					</Paragraph>
					{playlist.spotifyUrl && (
						<Button
							color="white"
							className={styles.spotifyLink}
							href={playlist.spotifyUrl}
							startIcon={<Spotify />}
							onClick={handleSpotifyAnalytics}
							rel="noopener noreferrer"
							size="S"
							target="_blank"
							variant="primary"
						>
							{t('spotify-link')}
						</Button>
					)}
				</div>

				<div className={styles.headerThumbnailWrapper}>
					<Image
						alt=""
						className={styles.headerThumbnail}
						priority
						src={optimizeContentfulImage(
							playlist.thumbnail?.url ?? playlist.background.url,
							{
								width: PLAYLIST_ARTWORK_WIDTH,
								height: PLAYLIST_ARTWORK_WIDTH,
							}
						)}
						height={PLAYLIST_ARTWORK_WIDTH}
						width={PLAYLIST_ARTWORK_WIDTH}
						style={{ maxHeight: '100%' }}
					/>

					{queue?.length > 0 && (
						<PlayButton
							aria-label={isPlaylistPlaying ? t('pause') : t('play')}
							size="L"
							className={styles.headerPlayButton}
							data-testid="play-all-button"
							isPlaying={isPlaylistPlaying}
							onClick={togglePlayAll}
						/>
					)}
				</div>
			</Container>
		</header>
	)
}

export default Header
